<template>
  <div id="dashboard-social-media">
    <!-- Filter -->
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center w-100">
        <div>
          <b-card-title class="mb-0 d-flex align-items-center">
            <i class="bi bi-phone"></i>
            <span style="margin-left: 5px"> Dashboard Social Media</span>
          </b-card-title>
        </div>
        <div class="d-flex flex-wrap gap-5px justify-content-end align-items-center">
          <b-form-select
            v-model="status"
            :options="statusOptions"
            class="mr-1"
            style="width: 150px;"
          ></b-form-select>
          <b-form-select
            v-model="platform"
            :options="platformOptions"
            class="mr-1"
            style="width: 150px;"
          ></b-form-select>
          <dateRangePicker :from_date="filter.from_date" :to_date="filter.to_date" @update-value="dateFilterChanged" />
        </div>
      </b-card-header>
    </b-card>

    <!-- KPI -->
    <SmdKpiCard :loading="loading.kpi" :data="data.kpi" :title="`Statistik ${platformText}`" />

    <div class="row">
      <!-- Trend -->
      <div class="col-md-8">
        <SmdTrendChart :loading="loading.trend" :data="data.trend" />
      </div>
      <!-- Recent Activity -->
      <div class="col-md-4">
        <SmdRecentActivityFeed :loading="loading.recent" :activities="data.recent" />
      </div>
    </div>

    <div class="row">
      <!-- Operator Trend Chart (Posts) -->
      <div class="col-md-6 col-12 mb-2">
        <SmdOperatorTrendChart :loading="loading.operatorTrend" :data="data.operatorTrend" />
      </div>
      <!-- Operator Trend Chart (Accounts) -->
      <div class="col-md-6 col-12 mb-2">
        <SmdOperatorAccountTrendChart :loading="loading.operatorAccountTrend" :data="data.operatorAccountTrend" />
      </div>
    </div>

    <!-- Top Users -->
    <SmdTopUserTable :loading="loading.topUsers" :users="data.topUsers" />
  </div>
</template>
<script>
import axios from "@/libs/axios";
import moment from "moment";
import { BFormSelect } from "bootstrap-vue";
import dateRangePicker from "@/views/components/dateRangePicker.vue";
import SmdKpiCard from "@/views/components/SmdKpiCard.vue";
import SmdTrendChart from "@/views/components/SmdTrendChart.vue";
import SmdTopUserTable from "@/views/components/SmdTopUserTable.vue";
import SmdRecentActivityFeed from "@/views/components/SmdRecentActivityFeed.vue";
import SmdOperatorTrendChart from "@/views/components/SmdOperatorTrendChart.vue";
import SmdOperatorAccountTrendChart from "@/views/components/SmdOperatorAccountTrendChart.vue";

export default {
  name: "DashboardSocialMedia",
  components: {
    BFormSelect,
    dateRangePicker,
    SmdKpiCard,
    SmdTrendChart,
    SmdTopUserTable,
    SmdRecentActivityFeed,
    SmdOperatorTrendChart,
    SmdOperatorAccountTrendChart
  },
  data() {
    return {
      status: "synced",
      statusOptions: [
        { value: "all", text: "Semua Status" },
        { value: "synced", text: "Synced" },
        { value: "new", text: "New" },
        { value: "error", text: "Error" },
      ],
      platform: "all",
      platformOptions: [
        { value: "all", text: "Semua Platform" },
        { value: "facebook", text: "Facebook" },
        { value: "instagram", text: "Instagram" },
      ],
      filter: {
        from_date: moment().startOf('day').format("YYYY-MM-DD HH:mm:ss"),
        to_date: moment().endOf('day').format("YYYY-MM-DD HH:mm:ss"),
      },
      loading: {
        kpi: false,
        trend: false,
        topUsers: false,
        recent: false,
        operatorTrend: false,
        operatorAccountTrend: false,
      },
      data: {
        kpi: {},
        trend: {},
        topUsers: [],
        recent: [],
        operatorTrend: { categories: [], series: {} },
        operatorAccountTrend: { categories: [], series: {} }
      }
    };
  },
  computed: {
    platformText() {
      if (this.platform === "all") return "Semua Platform";
      return this.platform === "facebook" ? "Facebook" : "Instagram";
    }
  },
  mounted() {
    this.fetchDashboardData();
  },
  watch: {
    status() {
      this.fetchDashboardData();
    },
    platform() {
      this.fetchDashboardData();
    }
  },
  methods: {
    dateFilterChanged(val) {
      this.filter.from_date = val.from_date;
      this.filter.to_date = val.to_date;
      this.fetchDashboardData();
    },
    async fetchDashboardData() {
      this.fetchKpi();
      this.fetchTrend();
      this.fetchTopUsers();
      this.fetchRecentActivity();
      this.fetchOperatorTrend();
      this.fetchOperatorAccountTrend();
    },
    async fetchOperatorTrend() {
      this.loading.operatorTrend = true;
      try {
        const res = await axios.get("/smd/operator-trend", { params: { status: this.status, platform: this.platform, ...this.filter } });
        this.data.operatorTrend = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.operatorTrend = false;
      }
    },
    async fetchOperatorAccountTrend() {
      this.loading.operatorAccountTrend = true;
      try {
        const res = await axios.get("/smd/operator-trend-account", { params: { status: this.status, platform: this.platform, ...this.filter } });
        this.data.operatorAccountTrend = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.operatorAccountTrend = false;
      }
    },
    async fetchKpi() {
      this.loading.kpi = true;
      try {
        const res = await axios.get("/smd/kpi", { params: { status: this.status, platform: this.platform, ...this.filter } });
        this.data.kpi = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.kpi = false;
      }
    },
    async fetchTrend() {
      this.loading.trend = true;
      try {
        const res = await axios.get("/smd/trend", { params: { status: this.status, platform: this.platform, ...this.filter } });
        this.data.trend = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.trend = false;
      }
    },
    async fetchTopUsers() {
      this.loading.topUsers = true;
      try {
        const res = await axios.get("/smd/top-users", { params: { status: this.status, platform: this.platform, ...this.filter } });
        this.data.topUsers = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.topUsers = false;
      }
    },
    async fetchRecentActivity() {
      this.loading.recent = true;
      try {
        const res = await axios.get("/smd/recent-activity", { params: { status: this.status, platform: this.platform, ...this.filter } });
        this.data.recent = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.recent = false;
      }
    }
  }
};
</script>
